import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from 'react-query';
import ModalWrapper from 'components/common/overlays/ModalWrapper';
import Dropdown from 'components/common/elements/Dropdown';
import SingleBtn from 'components/common/elements/SingleBtn';
import ChangePatient from './ChangePatient';
import styles from 'styles/components/common/overlays/Modal.module.scss';
import { ReactComponent as CloseIcon } from '../../assets/icons/close.svg';
import { STYLES_VARIABLES } from 'constants/styleVariables';
import { ChartInfo } from 'constants/chart/common/chartInfo';
import { TSelectObject } from 'types/dropdown';
import { TCheckSamePatientResponse } from 'types/apis/chart/studentEvaluationChart';
import { getPatientListByChart } from 'apis/student';
import useModal from 'hooks/useModal';
import useCheckChart, { TChangePatient } from 'hooks/chart/useCheckChart';
import useChartCreater from 'hooks/chart/useChartCreater';

type TProps = {
  courseInfoId: number;
  chartRound: number;
  registeredChartId: number;
  chartTypeNum: number;
};

const SelectPatient = ({
  courseInfoId,
  chartRound,
  registeredChartId,
  chartTypeNum,
}: TProps) => {
  const navigate = useNavigate();
  const { openModal, closeModal } = useModal();

  const [patientList, setPatientList] = useState<TSelectObject[]>([]);
  const [patient, setPatient] = useState<TSelectObject>();
  const [showPatientList, setShowPatientList] = useState(false);
  const [submit, setSubmit] = useState<boolean>(false);
  const [checkData, setCheckData] = useState<TCheckSamePatientResponse>();

  useQuery('getPatientListByChart', getPatientListByChart, {
    onSuccess: ({ data }) => {
      setPatientList(() =>
        data.map((item) => ({
          label: `${item.name} ${item.birthAndSex}`,
          value: item.patientId,
        }))
      );
    },
  });

  const goToChart = (evaluationChartId: number, evaluationRound: number) => {
    const chartRoute = ChartInfo.find((chart) => chart.type === chartTypeNum)
      ?.route;
    if (
      !chartRoute ||
      !evaluationChartId ||
      !patient?.value ||
      !courseInfoId ||
      !chartRound
    ) {
      openModal({
        title: '차트 조회 실패',
        text: '관리자에게 문의해 주세요.',
      });
      return;
    }
    sessionStorage.setItem('route', 'chart');
    // 차트 작성 페이지로 이동
    navigate(
      `/evaluation${chartRoute}/${evaluationChartId}/register?patientId=${patient?.value}&courseInfoId=${courseInfoId}&chartRound=${chartRound}&tab=${
        evaluationRound === 2 ? 2 : 0
      }`
    );
  };

  const onChangePatient = ({
    patientId,
    courseInfoId,
    chartTypeNum,
    registeredChartId,
    isError,
    data,
  }: TChangePatient) => {
    setCheckData(data);
    openModal({
      element: (
        <ChangePatient
          isError={isError}
          chartRound={data.chartRound}
          patientName={data.patientName || ''}
          chartCount={data.evaluationChartCnt}
          onConfirm={(password) =>
            chartCreater({
              isSamePatient: data.isSamePatient,
              password: password,
              evaluationChartIdList: data.evaluationChartIdList,
              registerRequest: {
                patientId: Number(patientId),
                courseInfoId,
                registeredChartId,
                chartTypeNum,
              },
            })
          }
        />
      ),
    });
  };

  const { chartCreater } = useChartCreater({
    registeredChartId,
    courseInfoId,
    patientId: patient?.value.toString(),
    chartTypeNum,
    checkData,
    goToChart,
    onChangePatient,
  });

  useCheckChart({
    courseInfoId,
    chartRound,
    registeredChartId,
    chartTypeNum,
    patientId: patient?.value.toString(),
    submit,
    setSubmit,
    goToChart,
    createChart: (data) => chartCreater(data),
    onChangePatient,
  });

  return (
    <ModalWrapper>
      <header className={styles.title_wrapper}>
        <h2 className={styles.title}>대상자를 선택해 주세요.</h2>
        <button type="button" onClick={closeModal}>
          <CloseIcon alt="close" fill={STYLES_VARIABLES.GRAY_07} />
        </button>
      </header>
      <p className="mb24 lh24">차트 작성을 위해서 대상자 선택이 필요합니다.</p>
      <Dropdown
        selectList={patientList}
        selectedObject={patient}
        showSelectList={showPatientList}
        setShowSelectList={setShowPatientList}
        btnStyle={{
          width: '100%',
          height: '48px',
        }}
        dropdownStyle={{ width: '432px' }}
        placeholder="대상자를 선택해 주세요."
        onClickButton={() => setShowPatientList(!showPatientList)}
        onClickSelectItem={(selected) => {
          setShowPatientList(false);
          setPatient(selected);
        }}
        defaultValue={patient?.label}
      />
      <footer className="modal_btn_wrapper mt24">
        <SingleBtn
          btnText="취소"
          hasOutline={true}
          btnStyle={{
            width: '100%',
            fontSize: '14px',
          }}
          onClick={closeModal}
        />
        <SingleBtn
          btnText="확인"
          isBtnActive={true}
          btnStyle={{
            width: '100%',
            fontSize: '14px',
          }}
          disabled={!patient}
          onClick={() => setSubmit(true)}
        />
      </footer>
    </ModalWrapper>
  );
};

export default SelectPatient;
